import { html, useEffect, useMemo, useRef, useState }
  from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';
import { RoleMarks } from '/rolemark.js';
import { Stepper } from '/stepper.js';

/**
 * A room's chat.
 *
 * A day of it, and then it is gone — the table has a TTL. That is the whole
 * design: this is for arranging a game, not for keeping a record, and a
 * permanent record of pub chatter is a thing somebody would eventually want
 * deleted from a log that cannot delete.
 *
 * Polled rather than pushed. A socket would mean a second connection to hold
 * open, a reconnect policy, and state on the server about who is listening,
 * for a room that will have three people in it. Five seconds is faster than
 * anybody types.
 */
const EVERY = 5000;

export function Chat({ base, me, title = 'Chat' }) {
  const [room, setRoom] = useState(null);
  const [draft, setDraft] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const foot = useRef(null);

  const path = `${base}/chat`;

  const pull = useMemo(() => () => get(path), [path]);

  useEffect(() => {
    let live = true;
    const tickle = () => pull()
      .then((r) => { if (live) { setRoom(r); setError(''); } })
      .catch((e) => { if (live) setError(e.detail || e.message); });
    setRoom(null);
    tickle();
    const tick = setInterval(tickle, EVERY);
    return () => { live = false; clearInterval(tick); };
  }, [path]);

  const said = room ? room.messages : null;
  const polls = (room && room.polls) || {};

  // The newest message is the one you want to see, so the view follows it.
  useEffect(() => {
    if (foot.current) foot.current.scrollIntoView({ block: 'end' });
  }, [said && said.length]);

  const refresh = async () => { setRoom(await get(path)); setError(''); };

  const send = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await post(path, { body });
      setDraft('');
      await refresh();
    } catch (e) {
      setError(e.detail || e.message);
    } finally { setBusy(false); }
  };

  const ask = async (poll) => {
    setBusy(true);
    try {
      await post(`${base}/poll`, poll);
      setAsking(false);
      await refresh();
    } catch (e) {
      setError(e.detail || e.message);
    } finally { setBusy(false); }
  };

  const vote = async (poll, option) => {
    // Optimistic would be wrong here: the count is the point, and a number
    // that moves and then moves back is worse than one that waits a moment.
    setBusy(true);
    try {
      await post(`${base}/vote`, { poll, option });
      await refresh();
    } catch (e) {
      setError(e.detail || e.message);
    } finally { setBusy(false); }
  };

  return html`
    <div class="stack" data-testid="circle-chat">
      <div class="rowbetween">
        <label>${title}</label>
        <button class="secondary" style="width:auto"
                data-testid="chat-ask-toggle"
                onClick=${() => setAsking(!asking)}>
          ${asking ? 'Cancel' : 'Poll'}
        </button>
      </div>
      <p class="sub" data-testid="chat-ttl">
        A day at a time — messages older than that are gone.
      </p>

      ${asking && html`<${AskPoll} onAsk=${ask} busy=${busy} />`}

      ${said === null
        ? html`<p class="sub">Loading…</p>`
        : said.length === 0
          ? html`<p class="empty" data-testid="chat-empty">
                   Nothing said yet.
                 </p>`
          : html`
            <div class="chat" data-testid="chat-log">
              ${said.map((m, i) => html`
                <div class=${`said ${m.user_id === me ? 'mine' : ''}`}
                     key=${`${m.ts}-${i}`} data-testid="chat-said">
                  <div class="who">
                    <span data-testid="chat-who">
                      ${/* Issue #59, capped at one: a chat line is narrow and
                            the name is the point of it. */''}
                      <${RoleMarks} roles=${m.roles} only=${1} size=${12} />
                      ${m.name || 'Someone'}
                    </span>
                    <span class="when" data-testid="chat-when">${at(m.ts)}</span>
                  </div>
                  ${m.kind === 'poll'
                    ? html`<${Poll} said=${m} me=${me}
                                    tally=${polls[(m.payload || {}).poll_id]}
                                    busy=${busy} onVote=${vote} />`
                    : html`<div data-testid="chat-body">${m.body}</div>`}
                </div>`)}
              <div ref=${foot}></div>
            </div>`}

      <div class="row pair">
        <input data-testid="chat-draft" value=${draft} maxlength="500"
               placeholder="Say something"
               onInput=${(e) => setDraft(e.target.value)}
               onKeyDown=${(e) => { if (e.key === 'Enter') send(); }} />
        <button data-testid="chat-send" disabled=${busy || !draft.trim()}
                onClick=${send}>Send</button>
      </div>

      ${error && html`<p class="error" data-testid="chat-error">${error}</p>`}
    </div>`;
}

/**
 * A poll, drawn from the tally the server worked out.
 *
 * Counts come from the server rather than being added up here, because two
 * screens looking at the same poll must not disagree about who is winning.
 */
function Poll({ said, me, tally, busy, onVote }) {
  const payload = said.payload || {};
  const counted = tally || { options: payload.options || [], counts: [],
                             by_user: {}, votes: 0, closed: false };
  const options = counted.options || [];
  const chosen = counted.by_user ? counted.by_user[me] : undefined;
  const total = counted.votes || 0;
  const shut = counted.closed || !left(counted.closes_at);

  return html`
    <div class="poll" data-testid="chat-poll"
         data-poll=${payload.poll_id || ''}>
      <div class="pollq" data-testid="poll-question">${said.body}</div>
      ${options.map((option, i) => {
        const n = (counted.counts || [])[i] || 0;
        // Share of the vote, not of the room: nobody knows how many people
        // are meant to answer, so a bar out of the votes cast is the only
        // honest one.
        const share = total ? Math.round((n / total) * 100) : 0;
        return html`
          <button key=${i} type="button"
                  class=${`polloption ${chosen === i ? 'chosen' : ''}`}
                  data-testid=${`poll-option-${i}`}
                  disabled=${busy || shut}
                  onClick=${() => onVote(payload.poll_id, i)}>
            <span class="fill" style=${`width:${share}%`}></span>
            <span class="polltext">${option}</span>
            <span class="pollcount" data-testid=${`poll-count-${i}`}>${n}</span>
          </button>`;
      })}
      <div class="sub" data-testid="poll-state">
        ${shut
          ? `Closed · ${total} ${total === 1 ? 'vote' : 'votes'}`
          : `${left(counted.closes_at)} left · ${total} ${
              total === 1 ? 'vote' : 'votes'}`}
      </div>
    </div>`;
}

/**
 * Asking one.
 *
 * Two answers to start with, because a poll with one answer is a statement.
 * Blank rows are dropped on send rather than policed as you type — being told
 * off for an empty box you have not reached yet is the worse of the two.
 */
function AskPoll({ onAsk, busy }) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [minutes, setMinutes] = useState(30);

  const put = (i, v) => setOptions(options.map((o, j) => (j === i ? v : o)));
  const ready = question.trim()
    && options.filter((o) => o.trim()).length >= 2;

  return html`
    <div class="card stack" data-testid="poll-form">
      <input data-testid="poll-question-input" value=${question}
             maxlength="200" placeholder="Ask something"
             onInput=${(e) => setQuestion(e.target.value)} />
      ${options.map((o, i) => html`
        <input key=${i} data-testid=${`poll-option-input-${i}`} value=${o}
               maxlength="60" placeholder=${`Answer ${i + 1}`}
               onInput=${(e) => put(i, e.target.value)} />`)}
      ${options.length < 8 && html`
        <button class="secondary" data-testid="poll-add-option"
                onClick=${() => setOptions([...options, ''])}>
          Another answer
        </button>`}
      <label>Open for</label>
      <${Stepper} testid="poll-minutes" label="minutes" value=${minutes}
                  min=${5} max=${720} step=${5} onChange=${setMinutes}
                  format=${(m) => (m < 60 ? `${m} min`
                    : `${m / 60} ${m === 60 ? 'hour' : 'hours'}`)} />
      <button data-testid="poll-ask" disabled=${busy || !ready}
              onClick=${() => onAsk({
                question: question.trim(),
                options: options.map((o) => o.trim()).filter(Boolean),
                minutes,
              })}>Ask</button>
    </div>`;
}

/** How long a poll has, in the coarsest unit that is still true. */
function left(closesAt) {
  if (!closesAt) return '';
  const ms = when(closesAt) - Date.now();
  if (!(ms > 0)) return '';
  const mins = Math.ceil(ms / 60000);
  if (mins < 60) return `${mins} min`;
  return `${Math.ceil(mins / 60)} h`;
}

function when(iso) {
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/** The time, not the date: everything here happened today by construction. */
function at(iso) {
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
